import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState, useRef } from 'react';
import { Header } from '@/components/header';
import { DynamicProgressSteps } from '@/components/ui/dynamicProgressSteps';
import { StepActions } from '@/components/patientApp/stepActions';
import { useDemoProcedure } from '@/hooks/useDemoProcedure';
import { useProcedureManager } from '@/hooks/useProcedureManager';
import { ProcedureScreen, ProcedureStepResponse, ProcedureStepType } from '@/types/procedure';
import { logger, openLinkInNewTab } from '@/lib/utils';
import { CueingStepStatus } from '@/api/schema/cueing';
import { PatientConnectionStatus } from '@/components/dashboard/patientConnectionStatus';
import { useTranslation } from 'react-i18next';
import { DemoDashboardCharts } from '@/components/dashboard/demoDashboardCharts';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import { assembleRoute } from '@/types/routes';

export const Route = createFileRoute('/_operator/subjects/$subjectId/procedures/$procedureId/demo/')({
  component: RouteComponent,
});

const DEMO_PHASES = [ 'instructions', 'demo' ];

interface DemoProcedureStep {
  id: string;
  name: string;
  status: CueingStepStatus;
  type?: ProcedureStepType;
}

export type DemoResponses = { value: number; confirmed: boolean }[];

function RouteComponent() {
  const { t } = useTranslation();
  const params = Route.useParams();
  const search = Route.useSearch();

  const { procedureId } = params as { subjectId: string; procedureId: string };
  const { checklistConfirmed } = search as { checklistConfirmed?: boolean };

  const { createDynamicDemoProcedure } = useDemoProcedure();
  const [ demoSteps, setDemoSteps ] = useState<DemoProcedureStep[]>([]);
  const initializationRef = useRef(false);
  const [ responses, setResponses ] = useState<DemoResponses>([]);

  const onStepResponse = useCallback((stepId: string, response: unknown) => {
    logger(`Step ${stepId} response:`, response);
  }, []);

  const onConnectionChange = useCallback((connected: boolean) => {
    logger('Connection status:', connected ? 'Connected' : 'Disconnected');
  }, []);

  const onResponse = useCallback((response: ProcedureStepResponse) => {
    setResponses((prevResponses) => {
      return [ ...prevResponses, { value: response.value, confirmed: response.confirmed } ];
    });
  }, []);

  const {
    currentStep,
    isConnected,
    isCompleted,
    isPaused,
    isStopped,
    connectionStatus,
    initializeProcedure,
    pauseProcedure,
    resumePausedProcedure,
    stopProcedure,
  } = useProcedureManager({
    onStepResponse,
    onConnectionChange,
    onResponse,
  });

  // Handle demo procedure initialization - only run once
  useEffect(() => {
    if (initializationRef.current) {
      return; // Already initialized
    }

    const handleStartDemo = async () => {
      try {
        const steps = await createDynamicDemoProcedure();
        const instructionsStepAction = {
          id: 'demo_info_instruction_1',
          name: 'Instructions',
          status: CueingStepStatus.NOT_STARTED,
          type: ProcedureStepType.INFO_INSTRUCTION,
        };

        const stepActions = [ instructionsStepAction, ...steps
          .filter(step => step.screen === ProcedureScreen.IMAGE_DISPLAY || step.screen === ProcedureScreen.VAS_SCALE)
          .map(step => ({
            id: step.id,
            name: step.screen === ProcedureScreen.IMAGE_DISPLAY
              ? 'Cueing'
              : 'VAS',
            status: CueingStepStatus.NOT_STARTED,
            type: step.type,
          })) ];

        setDemoSteps(stepActions);
        initializeProcedure(steps);
        initializationRef.current = true;

        if (!isConnected) {
          openLinkInNewTab(`/patientApp/${procedureId}`, 'patient-app-window');
        }
      }
      catch (error) {
        console.error('Failed to initialize demo procedure:', error);
      }
    };

    void handleStartDemo();
  }, []);

  useEffect(() => {
    if (isCompleted) {
      setDemoSteps(prevSteps =>
        prevSteps.map(step => ({
          ...step,
          status: CueingStepStatus.COMPLETED,
        })),
      );
      return;
    }

    if (currentStep) {
      setDemoSteps(prevSteps =>
        prevSteps.map((step) => {
          if (step.id === currentStep.id) {
            return { ...step, status: CueingStepStatus.IN_PROGRESS };
          }
          if (prevSteps.find(s => s.id === currentStep.id)?.id
            && prevSteps.indexOf(step) < prevSteps.findIndex(s => s.id === currentStep.id)) {
            return { ...step, status: CueingStepStatus.COMPLETED };
          }
          return step;
        }),
      );
    }
  }, [ currentStep, isCompleted ]);

  const getCurrentPhaseSteps = () => {
    const [ instructionsPhase, demoPhase ] = DEMO_PHASES;
    if (currentStep?.type === ProcedureStepType.INFO_INSTRUCTION) return instructionsPhase;
    return demoPhase;
  };

  const splitStepsByPhase = () => {
    const currentPhase = getCurrentPhaseSteps();
    if (currentPhase === DEMO_PHASES[0]) {
      return demoSteps.filter(step => step.type === ProcedureStepType.INFO_INSTRUCTION);
    }
    if (currentPhase === DEMO_PHASES[1]) {
      return demoSteps.filter(step => step.type !== ProcedureStepType.INFO_INSTRUCTION);
    }
    return [];
  };

  const getStepStatus = (step: DemoProcedureStep) => {
    if (step.id === currentStep?.id && isStopped) {
      return CueingStepStatus.STOPPED;
    }

    if (step.id === currentStep?.id && isPaused) {
      return CueingStepStatus.PAUSED;
    }

    return step.status;
  };

  return (
    <div className="bg-fill-surface-strong flex flex-col gap-space-lg">
      <Header button={ (
        <Button asChild>
          <Link
            from="/subjects/$subjectId/procedures/$procedureId/demo"
            to={ assembleRoute('operatorProcedure', { subjectId: params.subjectId, procedureId }) }
            search={ { checklistConfirmed } }
          >
            { t('finishDemo') }
          </Link>
        </Button>
      ) }
      />
      <div className="grid grid-cols-2 gap-space-lg">
        <div className="flex flex-col gap-space-lg">
          <DemoDashboardCharts procedureId={ procedureId } responses={ responses } />
          <PatientConnectionStatus status={ connectionStatus } />
        </div>

        <div className="flex flex-col gap-space-lg">
          <DynamicProgressSteps
            steps={ DEMO_PHASES.map(phase => (t(`dashboard.demoFlow.blocks.${phase}`))) }
            currentStep={ t(`dashboard.demoFlow.blocks.${getCurrentPhaseSteps()}`) }
          />
          <StepActions
            step={ t('dashboard.demoFlow.demoTitle') }
            showControlButtons={ false }
            onStopClick={ stopProcedure }

            onStopResumeClick={ () => {} }
            isStopped={ isStopped }
            onPauseClick={ pauseProcedure }
            onPauseResumeClick={ resumePausedProcedure }
            isPaused={ isPaused }
            stepPhases={ splitStepsByPhase().map(step => ({
              name: step.name,
              status: getStepStatus(step),
            })) }
          />
        </div>
      </div>
    </div>
  );
}
